<template>
  <main class="summary-composition">
    <mobile-header class="summary-composition__header">
      <span slot="back-button" class="custom-icon icon-close mr15" @click="closePage"></span>
      <span slot="header-title">{{ $t('dashboard.label.createTreatmentTab') }}</span>
    </mobile-header>
    <summary-info class="onlyXS mb30" label-key="dashboard.label.newTreatmentFor" />
    <div class="treatment-section m0a">
      <div class="treatment-section__header">
        <div
          class="treatment-section__header-label inline-block"
          :class="{ 'treatment-section__header-label__alma': isAlmaTerritory }"
        >
          {{ $t('dashboard.summary.treatmentComposition') }}
        </div>
      </div>
      <treatment-composition
        class="treatment-composition"
        :composition="treatmentComposition"
        :isAlmaTerritory="isAlmaTerritory"
      />
    </div>
    <mobile-fixed-footer class="summary-composition__footer flex--center--center">
      <button
        slot="footer"
        class="rounded-button-orange rounded-button-orange--filled onlyXS"
        :class="{
          'rounded-button-orange__alma rounded-button-orange__alma--filled': isAlmaTerritory
        }"
        @click="onContinueClick"
      >
        {{ $t('dashboard.action.next') }}
      </button>
    </mobile-fixed-footer>
  </main>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import MobileHeader from '@/modules/dashboard/components/common/MobileHeader';
import SummaryInfo from '@/modules/dashboard/components/create-treatment-tab/SummaryInfo';
import TreatmentComposition from '@/modules/dashboard/components/common/TreatmentComposition';
import MobileFixedFooter from '@/modules/dashboard/components/dashboard/common/MobileFixedFooter';

import { REVIEW_TREATMENT_MESSAGE_TYPE } from '@/modules/dashboard/api/constants';
import { types } from '@/modules/dashboard/store/types';
import rootTypes from '@/store/types';
import { ALMA_TERRITORIES } from '@/modules/dashboard/constants/alma-countries';
import treatmentPreviewMixin from '@/modules/dashboard/components/mixins/treatmentPreviewMixin';
import { get } from 'lodash';
import { getTreatmentType } from '@/modules/dashboard/utils/create-treatment-utils';
import { createTreatmentPreview } from '@/modules/dashboard/api';
import isAlmaTerritories from '@/modules/dashboard/components/mixins/isAlmaTerritories';

export default {
  name: 'SummaryTreatmentComposition',

  components: {
    MobileHeader,
    SummaryInfo,
    TreatmentComposition,
    MobileFixedFooter
  },
  mixins: [treatmentPreviewMixin, isAlmaTerritories],
  computed: {
    ...mapGetters({
      getSummaryTreatmentComposition: types.getters.SUMMARY_TREATMENT_COMPOSITION,
      currentPatientId: types.getters.CURRENT_PATIENT_ID,
      doctorCountry: rootTypes.getters.GET_USER_COUNTRY,
      userId: rootTypes.getters.GET_USER_ID,
      patientLocale: types.getters.CURRENT_PATIENT_LOCALE,
      treatmentId: types.getters.SUMMARY_TREATMENT_ID,
      summaryTreatmentId: types.getters.SUMMARY_TREATMENT_ID,
      recommendationDiagnostic: types.getters.RECOMMENDATION_DIAGNOSTIC,
      treatmentDirectoryRecommendations: types.getters.TREATMENT_DIRECTORY_RECOMMENDATIONS
    }),
    treatmentComposition() {
      return this.getSummaryTreatmentComposition({});
    },
    isAlmaTerritory() {
      return ALMA_TERRITORIES.includes(this.doctorCountry);
    }
  },
  methods: {
    ...mapActions({
      resetCreateTreatmentSelections: types.actions.RESET_CREATE_TREATMENT_SELECTIONS,
      setLoading: rootTypes.actions.SET_LOADING,
      setSummaryTreatmentId: types.actions.SET_SUMMARY_TREATMENT_ID,
      addRequestError: types.actions.ADD_ERROR
    }),
    closePage() {
      if (this.$route.query.summaryRenewTreatment) {
        this.resetCreateTreatmentSelections();
      }

      this.$router.go(-1);
    },
    async onContinueClick() {
      await this.createTreatmentPreview();

      if (this.isAlmaTerritory) {
        this.$router.push({
          name: 'ReviewPatientInviteTabs',
          params: {
            id: this.currentPatientId,
            templateType: REVIEW_TREATMENT_MESSAGE_TYPE.CREATE_TREATMENT
          }
        });
      } else {
        this.$router.push({
          name: 'SummaryTreatmentDetails'
        });
      }
    },
    async createTreatmentPreview() {
      this.setLoading(true);

      try {
        const participantId = get(this.currentPatientId, 'value', null);
        const diagnosticId = get(this.recommendationDiagnostic, 'id', null);

        const type = getTreatmentType({
          isTreatmentDirectory: !!this.treatmentDirectoryRecommendations,
          diagnosticId
        });

        const treatmentDirectoryId = get(
          this.treatmentDirectoryRecommendations,
          'treatmentRecommendationId',
          null
        );

        const treatment = {
          ...this.getTreatmentForPreview(),
          paid: false,
          startDate: this.isCustomDateSelected && this.date ? this.date : new Date(),
          started: !this.isCustomDateSelected,
          patientId: this.currentPatientId,
          treatmentDirectoryId,
          participantId,
          diagnosticId,
          type
        };

        const { data: treatmentId } = await createTreatmentPreview(
          this.currentPatientId,
          treatment
        );

        await this.setSummaryTreatmentId(treatmentId);
      } catch (error) {
        this.addRequestError('Treatment is not updated');
      } finally {
        this.setLoading(false);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/scss/helpers/colors';
@import '../../../../assets/scss/common/buttons';

.summary-composition {
  &__footer {
    display: none;
  }
}

.treatment-section {
  &__header {
    margin-bottom: 20px;
    text-align: center;
    border-bottom: 1px solid $border-light-grey-color;

    &-label {
      margin-bottom: -1px;
      padding-bottom: 10px;

      font-size: 14px;
      font-weight: 600;
      color: $text-color;

      border-bottom: 1px solid $orange-color;
    }

    &-label__alma {
      border-bottom: 1px solid $alma-color;
    }
  }
}

@media (max-width: 767px) {
  .summary-composition {
    padding: 60px 0 150px;

    .summary-composition__header {
      font-size: 13px;
      font-weight: 400;
      color: $text-color;
      background-color: $background;
    }

    &__footer {
      display: flex;
      height: 60px;

      background-color: $white;
      border-top: 1px solid $dim-white;
      box-shadow: 0px -6px 7px rgba(148, 166, 176, 0.15);
    }
  }

  .treatment-section {
    width: 100%;
    max-width: 350px;

    &__header {
      margin-bottom: 30px;

      &-label {
        padding-bottom: 15px;
        font-size: 12px;
      }
    }
  }

  .treatment-composition {
    padding: 0 20px;
  }
}
</style>
